import * as React from "react"

import type { VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import type { buttonVariants } from "./button"

type ButtonGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  orientation?: "horizontal" | "vertical"
  size?: VariantProps<typeof buttonVariants>["size"]
  variant?: VariantProps<typeof buttonVariants>["variant"]
}

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      className,
      children,
      orientation = "horizontal",
      size,
      variant,
      ...props
    },
    ref,
  ) => {
    const items = React.Children.toArray(children).filter(Boolean)

    return (
      <div
        ref={ref}
        role="group"
        data-slot="button-group"
        data-orientation={orientation}
        className={cn(
          "group/button-group inline-flex items-stretch",
          orientation === "vertical" ? "flex-col" : "flex-row",
          className,
        )}
        {...props}
      >
        {items.map((child, index) => {
          if (!React.isValidElement(child)) return child
          const isFirst = index === 0
          const isLast = index === items.length - 1

          const orientationClasses =
            orientation === "vertical"
              ? [
                  isFirst && "rounded-t-md",
                  isLast && "rounded-b-md",
                  !isFirst && "-mt-px",
                ]
              : [
                  isFirst && "rounded-s-md",
                  isLast && "rounded-e-md",
                  !isFirst && "-ml-px",
                ]

          const childProps: Record<string, unknown> = {
            className: cn(
              "rounded-none focus-visible:z-10",
              orientationClasses,
              child.props.className,
            ),
          }

          if (size && child.props.size === undefined) {
            childProps.size = size
          }

          if (variant && child.props.variant === undefined) {
            childProps.variant = variant
          }

          return React.cloneElement(child, childProps)
        })}
      </div>
    )
  },
)
ButtonGroup.displayName = "ButtonGroup"

export { ButtonGroup }
